
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import formatDateNumber from "@/app/infrastructures/misc/common-library/FormatDateNumber";
import { UrgentDeliveryManagementController } from "@/app/ui/controllers/UrgentDeliveryManagementController";
import { ApiStateData } from "@/domain/entities/Api";
import {
  UrgentDeliveryManagementRelabelData,
  UrgentDeliveryManagementRelabelList,
  UrgentDeliveryManagementRelabelListRequest,
  UrgentDeliveryManagementRelabelListRequestDownload
} from "@/domain/entities/UrgentDeliveryManagement";
import { Options, Vue } from "vue-class-component";
import { defineAsyncComponent } from "vue";
import { FlagPermissionUrgentDeliveryManagement } from "@/feature-flags/flag-permission-urgent-management";
import { RelabelPrintSize } from "@/app/ui/controllers/modules/relabel-print";
import { getHubData } from '@/app/infrastructures/misc/Cookies';
const UrgentRelabelFilter = defineAsyncComponent(() => import("./filter.vue"));

class RelabelParameters {
  search = "";
  clientAggregatorType = "";
  productType = "";
  originHubId = "";
  destinationHubId = "";
  originCityCode = "";
  destinationCityCode = "";
  startDate = "";
  endDate = "";
  startDateRaw: Date | null = null;
  endDateRaw: Date | null = null;
  orderBy = "";
  sort: "ASC" | "DESC" | "" = "";
}

@Options({
  components: {
    UrgentRelabelFilter
  }
})
export default class UrgentRelabel extends Vue {
  controller = UrgentDeliveryManagementController;

  listData = new UrgentDeliveryManagementRelabelList();
  apiListData = new ApiStateData();
  parameters = new RelabelParameters();
  printSizes: RelabelPrintSize[] = ["A6", "A4"];
  printSttNo = "";
  isPrinting = false;
  declare $refs: {
    printMenu: any;
  };

  get hasActions(): boolean {
    return (
      FlagPermissionUrgentDeliveryManagement.permission_urgent_delivery_management_relabel_enable.isEnabled() &&
      !!this.listData.data.length &&
      !this.apiListData.loading &&
      !this.apiListData.errorType &&
      !this.isPrinting
    );
  }

  get tableColumns() {
    return this.listData.data.length
      ? this.columns
      : this.columns.filter((column) => column.key !== "button_column");
  }

  get columns() {
    return [
      {
        name: this.$t("urgentRelabel.column.no"),
        styleHead: "w-16 text-left text-14px whitespace-nowrap",
        render: (_: UrgentDeliveryManagementRelabelData, i: number) =>
          `<span class="font-normal text-black-lp-300 whitespace-nowrap">${i +
            1 +
            this.listData.pagination.limit *
              (this.listData.pagination.page - 1)}</span>`
      },
      {
        name: `<div class='font-semibold text-black-lp-300 text-14px'>${this.$t(
          "urgentRelabel.column.sttNo"
        )}</div><div class='text-12px text-black-lp-300'>${this.$t(
          "urgentRelabel.column.shipmentType"
        )}</div>`,
        styleHead: "w-40 text-left text-14px whitespace-nowrap",
        render: (item: UrgentDeliveryManagementRelabelData) =>
          `<div class="font-normal text-sm text-black-lp-100">${item.pdSttNo}</div><div class="text-xs text-gray-lp-600">${item.productType}</div>`
      },
      {
        name: this.$t("urgentRelabel.column.rootSttNo"),
        styleHead: "w-40 text-left text-14px whitespace-nowrap",
        render: (item: UrgentDeliveryManagementRelabelData) =>
          `<div class="font-normal text-sm text-black-lp-100">${item.rootReverseSttNo}</div>`
      },
      {
        name: this.$t("urgentRelabel.column.processedBy"),
        styleHead: "w-32 text-left text-14px whitespace-nowrap",
        render: (item: UrgentDeliveryManagementRelabelData) =>
          `<div class='rounded bg-gray-lp-1600 text-black-lp-300 py-1 px-2 text-14px'>${item.processedByLabel}</div>`
      },
      {
        name: this.$t("urgentRelabel.column.overtimeDuration"),
        styleHead: "w-32 text-left text-14px whitespace-nowrap",
        render: (item: UrgentDeliveryManagementRelabelData) =>
          `<div class="font-normal text-sm text-black-lp-100">${this.$t("urgentRelabel.column.overtimeDurationDays", {day: item.overtimeDurationDays})}</div>`
      },
      {
        name: this.$t("urgentRelabel.column.bkdDate"),
        styleHead: "w-40 text-left text-14px whitespace-nowrap",
        render: (item: UrgentDeliveryManagementRelabelData) =>
          `<div class="font-normal text-sm text-black-lp-100">${
            item.formatBkdAt
          }</div><div class="text-xs text-gray-lp-600">${item.originHubName ||
            "-"}</div>`
      },
      {
        name: this.$t("urgentRelabel.column.originCity"),
        styleHead: "w-40 text-left text-14px whitespace-nowrap",
        render: (item: UrgentDeliveryManagementRelabelData) =>  {
          return `<div class="font-normal text-sm text-black-lp-100">${[
            item.originCityCode,
            item.originCityName
          ]
            .filter(Boolean)
            .join(" - ")}</div>`;
        }
      },
      {
        name: this.$t("urgentRelabel.column.destinationCity"),
        styleHead: "w-40 text-left text-14px whitespace-nowrap",
        render: (item: UrgentDeliveryManagementRelabelData) => {
          return `<div class="font-normal text-sm text-black-lp-100">${[
            item.destinationCityCode,
            item.destinationCityName
          ]
            .filter(Boolean)
            .join(" - ")}</div>`;
        }
      },
      {
        name: this.$t("urgentRelabel.column.printReceipt"),
        key: "button_column",
        styleHead: "w-24 text-left text-14px whitespace-nowrap",
        styleCustom: "padding-action-sti",
        styleButton: (item: UrgentDeliveryManagementRelabelData) => ({
          outline: true,
          icon: "printer",
          title: "",
          textColor: "black-lp-300",
          color: "gray-lp-1600",
          customClass: "button-option-sti",
          disabled: !this.hasActions,
          "data-testid": "urgent-delivery-monitor-relabel-print-row",
          clickFunction: () => null,
          groupOptions: this.printSizes.map((size) => ({
            icon: "printer",
            label: this.$t("urgentRelabel.printSize", { size }),
            clickFunction: () => this.onPrint(size, item.pdSttNo),
            dataTestid: `urgent-delivery-monitor-relabel-print-row-${size.toLowerCase()}`
          }))
        })
      }
    ];
  }

  mounted(): void {
    this.apiListData.loading = true;
    this.fetchList();
  }

  async fetchList(): Promise<void> {
    if (
      !FlagPermissionUrgentDeliveryManagement.permission_urgent_delivery_management_relabel_enable.isEnabled()
    ) {
      this.apiListData.loading = false;
      return;
    }
    this.apiListData.loading = true;
    try {
      const resp = await this.controller.getRelabelList(this.listRequest);
      this.listData = resp;
      this.apiListData.errorType = "";
    } catch (error) {
      this.apiListData.errorType = parsingErrorResponse(error).type;
    } finally {
      this.apiListData.loading = false;
    }
  }

  get emptyStateData(): {
    headerMessage: string;
    message: string;
    icon: string;
  } {
    return {
      headerMessage: this.$t("urgentRelabel.empty.header"),
      message: this.$t("urgentRelabel.empty.message"),
      icon: "noData_empty"
    };
  }

  // HANDLERS
  onSearch(value: string): void {
    if (!value.length || value.length >= 3) {
      this.listData.pagination.page = 1;
      this.parameters.search = value;
      this.fetchList();
    }
  }

  clearSearch(): void {
    this.listData.pagination.page = 1;
    this.parameters.search = "";
    this.fetchList();
  }

  onApplyFilter(params: any): void {
    this.listData.pagination.page = 1;

    this.parameters.clientAggregatorType =
      params.clientAggregatorType?.value || "";

    this.parameters.productType = Array.isArray(params.productType)
      ? params.productType.join(",")
      : "";

    this.parameters.originHubId = Array.isArray(params.originHubIds)
      ? params.originHubIds.join(",") : "";
    this.parameters.destinationHubId = Array.isArray(params.destinationHubIds)
      ? params.destinationHubIds.join(",") : "";

    this.parameters.originCityCode = Array.isArray(params.originCityCodes)
      ? params.originCityCodes.join(",")
      : "";

    this.parameters.destinationCityCode = Array.isArray(
      params.destinationCityCodes
    )
      ? params.destinationCityCodes.join(",")
      : "";

    this.onDateSelect([
      params.dateRange?.start || null,
      params.dateRange?.end || null
    ]);
  }

  // Resets search and date range too, not only the filter panel's own
  // fields: the AC's Reset scenario ("search dan filter menjadi kosong")
  // covers the whole query bar, and there is only one Reset control
  // (inside the filter panel) per the detailing's data-testid table.
  onResetFilter(): void {
    this.listData.pagination.page = 1;
    this.parameters.search = "";
    this.parameters.clientAggregatorType = "";
    this.parameters.productType = "";
    this.parameters.originHubId = "";
    this.parameters.destinationHubId = "";
    this.parameters.originCityCode = "";
    this.parameters.destinationCityCode = "";
    this.parameters.startDate = "";
    this.parameters.endDate = "";
    this.parameters.startDateRaw = null;
    this.parameters.endDateRaw = null;
    this.parameters.orderBy = "";
    this.parameters.sort = "";
    this.fetchList();
  }

  onDateSelect(dates: Array<Date | null>): void {
    this.listData.pagination.page = 1;

    if (!dates?.[0] || !dates?.[1]) {
      this.parameters.startDate = "";
      this.parameters.endDate = "";
      this.parameters.startDateRaw = null;
      this.parameters.endDateRaw = null;
    } else {
      const [start, end] = dates;
      this.parameters.startDate = formatDateNumber(start);
      this.parameters.endDate = formatDateNumber(end);
      this.parameters.startDateRaw = start;
      this.parameters.endDateRaw = end;
    }

    this.fetchList();
  }

  onSort(sort: { sortBy: string; sortDirection: "ASC" | "DESC" | "" }): void {
    this.listData.pagination.page = 1;
    this.parameters.orderBy = sort.sortBy;
    this.parameters.sort = sort.sortDirection;
    this.fetchList();
  }

  async onDownload(): Promise<void> {
    if (!this.hasActions) return;
    const { page, limit, ...parameters } = this.listRequest;
    await this.controller.downloadUrgentRelabelList(
      new UrgentDeliveryManagementRelabelListRequestDownload(parameters)
    );
  }

  get listRequest(): UrgentDeliveryManagementRelabelListRequest {
    return new UrgentDeliveryManagementRelabelListRequest({
      page: this.listData.pagination.page,
      limit: 20,
      sttNo: this.parameters.search,
      clientAggregatorType: this.parameters.clientAggregatorType,
      productType: this.parameters.productType,
      originCityCode: this.parameters.originCityCode,
      destinationCityCode: this.parameters.destinationCityCode,
      startDate: this.parameters.startDate,
      endDate: this.parameters.endDate,
      hubId: getHubData()?.hubId || "0",
      ...(this.parameters.sort && this.parameters.orderBy
        ? { orderBy: this.parameters.orderBy, sortBy: this.parameters.sort }
        : {})
    });
  }

  async openPrintMenu(event: Event, sttNo = ""): Promise<void> {
    if (!this.hasActions) return;
    const currentTarget = event.currentTarget;
    if (!sttNo &&
      !(await this.controller.canPrintRelabel(
        this.listData.data.length
      ))
    )
      return;
    this.printSttNo = sttNo;
    this.$refs.printMenu.toggle({ currentTarget });
  }

  async onPrint(size: RelabelPrintSize, sttNo = this.printSttNo): Promise<void> {
    if (!this.hasActions) return;
    this.$refs.printMenu.hide();
    this.isPrinting = true;
    try {
      await this.controller.printRelabel({
        size,
        sttNos: this.listData.data.map((item) => item.pdSttNo),
        sttNo: sttNo || undefined
      });
    } finally {
      this.isPrinting = false;
    }
  }
}
